import type { AppUser, DSUser } from '@/types'
import type { AuthError } from 'firebase/auth'
import useUserStore from '@/composables/store/useUserStore'
import {
  getIdToken,
  getRedirectResult,
  GoogleAuthProvider,
  inMemoryPersistence,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth'

export default function useLogin() {
  const { $posthog } = useNuxtApp()
  const config = useRuntimeConfig()

  const sessionUser = useSessionUser()
  const { setUser, clearUser } = useUserStore()
  const { timeDiff } = useDateHelper()
  const auth = useFirebaseAuth()!
  const provider = new GoogleAuthProvider()
  const authError = ref<AuthError | null>(null)

  const identifyPosthogUser = (user: DSUser | null) => {
    if (!user)
      return
    const posthog = $posthog()
    const payload = <{
      retentionType: 'new' | 'returning' | null
      email: string | null
    }>{
      retentionType: null,
    }

    const diff = timeDiff(Date.now(), user.createdTime!.getTime(), 'm')
    // check if the user's created time is within 10min of the current time
    if (diff < 10) {
      payload.retentionType = 'new'

      posthog.capture('signup', {
        loginMethod: 'google',
      })
    } else {
      payload.retentionType = 'returning'
    }

    payload.email = user.email
    posthog.identify(user.id, payload)
  }

  const setLoginToken = async (firebaseIdToken: string) => {
    // use id token from firebase and set it to the cookie
    const { data } = await useFetch<AppUser>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ firebaseIdToken }),
    })

    sessionUser.value = data.value as AppUser

    return sessionUser.value
  }

  const redirectToGoogleLogin = async () => {
    await setPersistence(auth, inMemoryPersistence)
    try {
      await signInWithRedirect(auth, provider)
    } catch (e: unknown) {
      authError.value = e as AuthError
      console.error(e)
    }
  }

  const loginWithGoogle = async () => {
    // redirect login is not supported in dev mode, so we use popup login
    if (config.public.signInWithPopup) {
      try {
        const result = await signInWithPopup(auth, provider)
        const firebaseIdToken = await getIdToken(result.user)
        const appUser = await setLoginToken(firebaseIdToken)
        const user = await setUser(appUser)
        identifyPosthogUser(user)
      } catch (e: unknown) {
        authError.value = e as AuthError
        console.error(e)
      }
    } else {
      await redirectToGoogleLogin()
    }

    return sessionUser.value
  }

  // check redirect result in case of redirect login
  const checkRedirectResult = async () => {
    try {
      const result = await getRedirectResult(auth)
      if (!result)
        return null

      const appUser = await setLoginToken(await getIdToken(result.user))
      const user = await setUser(appUser)
      identifyPosthogUser(user)

      return appUser
    } catch (e: unknown) {
      authError.value = e as AuthError
      console.error(e)
      setUser(null)
      return null
    }
  }

  const logout = async () => {
    try {
      await signOut(auth)
      await $fetch('/api/auth/logout', {
        method: 'POST',
      })
      clearUser()
      sessionUser.value = null
    } catch (e: unknown) {
      authError.value = e as AuthError
      console.error(e)
    }
  }

  const checkUser = async () => {
    if (sessionUser.value)
      return

    const { data } = await useFetch<AppUser>('/api/auth/me', {
      headers: useRequestHeaders(['cookie']) as HeadersInit,
    })

    sessionUser.value = data.value
  }

  return {
    loginWithGoogle,
    redirectToGoogleLogin,
    checkRedirectResult,
    setLoginToken,
    checkUser,
    logout,
    authError,
  }
}
